import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { fmtINRFull } from '@/lib/format';
import { MapPin, Download } from 'lucide-react';

/**
 * Iter41 — Visit reconciliation strip. Renders on WO detail, Project
 * detail, and /visits page. Shows the WO's included allowance vs actual
 * completed / cancelled-on-arrival visits + additional billable value.
 *
 * Props:
 *   projectId   required
 *   compact     (default false) → single-line strip
 */
export function VisitReconciliationStrip({ projectId, compact = false }) {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    api.get(`/visits/reconciliation/${projectId}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [projectId]);

  const downloadPdf = async () => {
    if (!projectId || downloading) return;
    setDownloading(true);
    try {
      const resp = await api.get(`/visits/${projectId}/reconciliation.pdf`,
                                  { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data],
                                                       { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `visit_reconciliation_${projectId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      /* silent — user can retry */
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return null;

  const {
    included_visits, completed_visits, cancelled_on_arrival_visits,
    completed_included, included_remaining,
    additional_completed, additional_basis, additional_fixed_fee_inr,
    total_additional_billable_inr, additional_fixed_value_inr,
    additional_actuals_value_inr, gst_on_additional_inr, value_masked,
  } = data;

  return (
    <div className="border rounded-md p-3 bg-[hsl(var(--surface-1))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`visit-reconciliation-${projectId}`}>
      <div className="text-[11.5px] font-medium text-[hsl(var(--foreground-muted))] mb-1.5 flex items-center justify-between gap-1">
        <span className="flex items-center gap-1">
          <MapPin size={11} /> Visit reconciliation
        </span>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={downloading}
          className="text-[11px] px-2 py-0.5 rounded border hover:bg-[hsl(var(--surface-2))] inline-flex items-center gap-1 disabled:opacity-50"
          style={{ borderColor: 'hsl(var(--border))' }}
          data-testid={`visit-recon-pdf-${projectId}`}
          title="Download client-shareable PDF">
          <Download size={10} />
          {downloading ? 'Preparing…' : 'PDF'}
        </button>
      </div>
      <div className={`grid ${compact ? 'grid-cols-4' : 'grid-cols-2 md:grid-cols-4'} gap-3 text-[12.5px]`}>
        <Stat label="Included allowed"    value={included_visits} />
        <Stat label="Completed"           value={completed_visits} />
        <Stat label="Cancelled on arrival" value={cancelled_on_arrival_visits}
               sub={cancelled_on_arrival_visits > 0 ? 'consumed allowance' : ''} />
        <Stat label="Included remaining"  value={included_remaining}
               emphasize={included_remaining === 0 && additional_completed > 0} />
        <Stat label="Additional completed" value={additional_completed}
               sub={additional_basis ? additional_basis.replace(/_/g, ' ') : ''} />
        {!value_masked && (
          <>
            <Stat label="Additional fixed fee ₹"
                   value={fmtINRFull(additional_fixed_fee_inr)} />
            <Stat label="Additional billable ₹"
                   value={fmtINRFull(total_additional_billable_inr)}
                   sub={additional_actuals_value_inr > 0
                        ? `${fmtINRFull(additional_fixed_value_inr)} fixed + ${fmtINRFull(additional_actuals_value_inr)} actuals`
                        : ''}
                   emphasize={total_additional_billable_inr > 0} />
            <Stat label="GST on additional"
                   value={fmtINRFull(gst_on_additional_inr)} />
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, emphasize, sub }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))]">{label}</div>
      <div className={`text-[13.5px] tabular-nums ${emphasize ? 'font-semibold text-[hsl(var(--warning))]' : ''}`}>
        {value ?? '—'}
      </div>
      {sub && <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))] mt-0.5">{sub}</div>}
    </div>
  );
}
